import type {
  CompanionFavorite,
  CompanionNavigatePayload,
  CompanionNotificationPayload,
  CompanionPageContext,
  CompanionPreferenceChangedPayload,
  CompanionSyncPayload,
  CompanionViewChangedPayload,
} from '@/lib/benny-companion/events'
import { useEffect, useMemo, useRef } from 'react'
import { useCurrentPageSnapshot } from '@/components/assistant/PageSnapshotProvider'
import { useTimeEntry } from '@/components/time-entry'
import { calculateElapsed } from '@/components/time-entry/TimeEntryProvider'
import {
  saveBennyCompanionPreferences,
  saveBennyCompanionView,
} from '@/hooks/useBennyPreference'
import { useFavorites } from '@/hooks/useFavorites'
import {
  BENNY_COMPANION_EVENTS,
  getCompanionPersonality,
  getCompanionQuietUntil,
  normalizeCompanionPreferenceChange,
} from '@/lib/benny-companion/events'
import { createLogger } from '@/lib/logger'
import { getNotificationRoute } from '@/lib/notifications/notification-route'
import { isOwnedNotificationDeliveryCurrent } from '@/lib/notifications/owned-notification-delivery'
import {
  focusMainWindow,
  getBennyCompanionSettings,
  isTauri,
  showBennyCompanion,
} from '@/lib/utils/tauri'
import { stopRecording } from '@/lib/utils/tauri-recording'
import { useAuth } from '@/store/auth.store'
import { useBennyStore } from '@/store/benny.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingStore } from '@/store/recording.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'
import { useLocation, useMatches, useNavigate } from 'react-router-dom'

import type { AppPreferences, Notification } from '@scrambl/shared'

const logger = createLogger('benny-companion')

/**
 * Runs in the main desktop window. Restores the floating Benny companion on
 * startup and acts as the bridge between app state and the companion window:
 * streams timer / assistant activity snapshots out, and handles open-chat /
 * navigate intents coming back from the companion.
 */
export function BennyCompanionController() {
  const hasRestored = useRef(false)
  const navigate = useNavigate()
  const location = useLocation()
  const matches = useMatches()
  const { activeEntry } = useTimeEntry({ liveElapsed: false })
  const companionActive = useBennyStore(s => s.companionActive)
  const assistantThinking = useBennyStore(s => s.streamingState !== null)
  // Subscribe only to the discrete state (not durationMs/levels, which tick
  // constantly) — elapsed is read via getState() at emit time.
  const recordingState = useRecordingStore(s => s.state)
  // Live favorites, resolved with names — mirrors the dashboard widget. The
  // hook also mounts the entity/project queries + loads the favorites store,
  // so the companion gets a fresh list even if no widget is on screen.
  const { favoriteEntities, favoriteProjects, favoriteModules } = useFavorites({
    enabled: companionActive,
  })
  const favorites = useMemo<CompanionFavorite[]>(() => {
    const items: CompanionFavorite[] = []
    const seen = new Set<string>()
    const push = (fav: CompanionFavorite) => {
      const key = `${fav.type}:${fav.id}`
      if (seen.has(key)) return
      seen.add(key)
      items.push(fav)
    }
    for (const m of favoriteModules) {
      const type =
        m.type === 'entity'
          ? 'ENTITY'
          : m.type === 'project'
            ? 'PROJECT'
            : m.type
      push({
        id: m.id,
        name: m.name,
        type,
        parentName: m.parentName,
        identifier: m.identifier,
      })
    }
    for (const e of favoriteEntities) {
      push({ id: e.id, name: e.name, type: 'ENTITY' })
    }
    for (const p of favoriteProjects) {
      push({
        id: p.id,
        name: p.name,
        type: 'PROJECT',
        parentName: p.entityName,
      })
    }
    return items
  }, [favoriteEntities, favoriteProjects, favoriteModules])
  const orgId = useOrganizationStore(state => state.currentOrganization?.id)
  const orgSlug = useOrganizationStore(state => state.currentOrganization?.slug)

  // Same param-merging BennyChat does for its own aiContext — the companion
  // chat rebuilds context from these ingredients so Benny can reference and
  // act on whatever the user is looking at in the main window.
  const routeParams = useMemo(
    () =>
      matches.reduce<Record<string, string | undefined>>(
        (acc, match) => ({ ...acc, ...(match.params || {}) }),
        {},
      ),
    [matches],
  )
  const { snapshot: pageSnapshot } = useCurrentPageSnapshot()
  const pageContext: CompanionPageContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      title: document.title,
      params: routeParams,
      pageSnapshot,
    }),
    [
      location.pathname,
      location.search,
      location.hash,
      routeParams,
      pageSnapshot,
    ],
  )
  const preferences = useUserPreferencesStore(s => s.preferences)
  const userId = useAuth(s => s.user?.id ?? null)
  const appPreferences = getUserAppPreference(preferences)?.preferences as
    | AppPreferences
    | undefined
  const bennyNotificationsEnabled =
    appPreferences?.bennyNotificationsEnabled === true
  const companionView = appPreferences?.bennyCompanionView ?? null
  const personality = getCompanionPersonality(
    appPreferences?.bennyCompanionPersonality,
  )
  const quietUntil = getCompanionQuietUntil(
    appPreferences?.bennyCompanionQuietUntil,
  )

  // Latest state for event handlers registered once on mount.
  const stateRef = useRef({
    activeEntry,
    assistantThinking,
    recordingState,
    orgId,
    orgSlug,
    pageContext,
    companionView,
    favorites,
  })
  useEffect(() => {
    stateRef.current = {
      activeEntry,
      assistantThinking,
      recordingState,
      orgId,
      orgSlug,
      pageContext,
      companionView,
      favorites,
    }
  }, [
    activeEntry,
    assistantThinking,
    recordingState,
    orgId,
    orgSlug,
    pageContext,
    companionView,
    favorites,
  ])

  const emitSync = async () => {
    const {
      activeEntry,
      assistantThinking,
      recordingState,
      orgId,
      orgSlug,
      pageContext,
      companionView,
      favorites,
    } = stateRef.current
    const isRecording =
      recordingState === 'recording' || recordingState === 'paused'
    // Read persisted preferences at emission time so a save acknowledgment
    // cannot accidentally send the previous React render's values.
    const currentUserId = useAuth.getState().user?.id ?? null
    const savedPreference = getUserAppPreference(
      useUserPreferencesStore.getState().preferences,
    )
    const savedPreferences =
      currentUserId &&
      (!savedPreference?.userId || savedPreference.userId === currentUserId)
        ? (savedPreference?.preferences as AppPreferences | undefined)
        : undefined
    const payload: CompanionSyncPayload = {
      userId: currentUserId,
      assistantThinking,
      recording: isRecording
        ? {
            active: true,
            isPaused: recordingState === 'paused',
            elapsedSeconds: Math.round(
              useRecordingStore.getState().durationMs / 1000,
            ),
          }
        : null,
      timer: activeEntry
        ? {
            running: true,
            isPaused: activeEntry.isPaused === true,
            elapsedSeconds: calculateElapsed(activeEntry),
            label:
              activeEntry.description ||
              activeEntry.linkedModules?.[0]?.moduleName ||
              null,
            route: orgSlug ? `/${orgSlug}/my-time` : null,
            moduleIds: [
              activeEntry.moduleId,
              ...(activeEntry.linkedModules ?? []).map(m => m.moduleId),
            ].filter((id): id is string => !!id),
          }
        : null,
      pageContext,
      organization: orgId && orgSlug ? { id: orgId, slug: orgSlug } : null,
      companionView,
      personality: getCompanionPersonality(
        savedPreferences?.bennyCompanionPersonality,
      ),
      quietUntil: getCompanionQuietUntil(
        savedPreferences?.bennyCompanionQuietUntil,
      ),
      favorites,
    }
    const { emit } = await import('@tauri-apps/api/event')
    await emit(BENNY_COMPANION_EVENTS.sync, payload)
  }
  const emitSyncRef = useRef(emitSync)
  useEffect(() => {
    emitSyncRef.current = emitSync
  })

  // Restore the companion window on startup if the user has it enabled.
  useEffect(() => {
    if (!isTauri() || hasRestored.current) return
    hasRestored.current = true

    let cancelled = false
    void getBennyCompanionSettings().then(settings => {
      if (cancelled || settings?.enabled !== true) return

      void showBennyCompanion().catch(error => {
        logger.error('showBennyCompanion failed', error)
      })
    })

    return () => {
      cancelled = true
    }
  }, [])

  // Companion → main intents, and companion lifecycle tracking.
  useEffect(() => {
    if (!isTauri()) return undefined

    let cancelled = false
    const unlisteners: Array<() => void> = []

    const setup = async () => {
      const { listen } = await import('@tauri-apps/api/event')
      const { getCurrentWindow } = await import('@tauri-apps/api/window')
      // Only the primary window should react to companion intents —
      // otherwise every open window would navigate.
      if (getCurrentWindow().label !== 'main') return

      unlisteners.push(
        await listen(BENNY_COMPANION_EVENTS.ready, () => {
          if (cancelled) return
          logger.debug('companion ready — sending state snapshot')
          useBennyStore.getState().setCompanionActive(true)
          void emitSyncRef.current().catch(error => {
            logger.error('companion sync failed', error)
          })
        }),
        await listen(BENNY_COMPANION_EVENTS.disabled, () => {
          if (cancelled) return
          logger.debug('received `benny-companion-disabled` event')
          useBennyStore.getState().setCompanionActive(false)
        }),
        await listen(BENNY_COMPANION_EVENTS.openChat, () => {
          if (cancelled) return
          logger.debug('received `open-benny-chat` event')
          void focusMainWindow()
          useBennyStore.getState().setOpen(true)
        }),
        await listen<CompanionNavigatePayload>(
          BENNY_COMPANION_EVENTS.navigate,
          event => {
            if (cancelled) return
            const route = event.payload?.route
            logger.debug('companion navigate', route)
            void focusMainWindow()
            if (route) navigate(route)
          },
        ),
        await listen<CompanionViewChangedPayload>(
          BENNY_COMPANION_EVENTS.viewChanged,
          event => {
            if (cancelled) return
            const view = event.payload?.view
            if (view !== 'chat' && view !== 'upcoming' && view !== 'favorites')
              return

            // Persist to the synced user preferences so the companion
            // reopens on this view after a relaunch (and on other devices).
            const store = useUserPreferencesStore.getState()
            const existing = (getUserAppPreference(store.preferences)
              ?.preferences ?? {}) as AppPreferences
            if (existing.bennyCompanionView === view) return

            logger.debug('persisting companion view', view)
            void saveBennyCompanionView(view).then(saved => {
              if (!saved) logger.error('failed to persist companion view')
            })
          },
        ),
        await listen<CompanionPreferenceChangedPayload>(
          BENNY_COMPANION_EVENTS.preferenceChanged,
          event => {
            if (cancelled) return
            const change = normalizeCompanionPreferenceChange(event.payload)
            const requestedBy = useAuth.getState().user?.id
            const save = async () => {
              const saved = change
                ? await saveBennyCompanionPreferences(change)
                : false
              if (cancelled || requestedBy !== useAuth.getState().user?.id)
                return
              await emitSyncRef.current()
              if (!saved) {
                const { emit } = await import('@tauri-apps/api/event')
                await emit(BENNY_COMPANION_EVENTS.preferenceSaveFailed, {
                  message: "I couldn't save that setting. Try again.",
                })
              }
            }
            void save().catch(error =>
              logger.error('failed to save companion preferences', error),
            )
          },
        ),
        await listen(BENNY_COMPANION_EVENTS.discardRecording, () => {
          if (cancelled) return
          logger.debug('companion requested recording discard')
          const sleep = (ms: number) =>
            new Promise(resolve => setTimeout(resolve, ms))
          const discard = async () => {
            // Right after a companion-initiated start the recording store may
            // not have a recordingId yet — poll briefly so the discard mark
            // isn't skipped (an unmarked recording would upload anyway).
            let id = useRecordingStore.getState().recordingId
            let attempts = 0
            while (!id && attempts < 10) {
              if (cancelled) return
              await sleep(250)
              id = useRecordingStore.getState().recordingId
              attempts++
            }
            if (id) {
              useRecordingStore.getState().markDiscarded(id)
            } else {
              logger.warn(
                'no recordingId after polling — stopping without discard mark',
              )
            }
            try {
              await stopRecording()
            } catch (error) {
              logger.error('companion-requested discard failed', error)
              return
            }
            // Ack so the companion knows the discard completed.
            const { emit } = await import('@tauri-apps/api/event')
            await emit(BENNY_COMPANION_EVENTS.recordingDiscarded)
          }
          void discard().catch(error => {
            logger.error('companion-requested discard failed', error)
          })
        }),
      )
    }

    void setup()

    return () => {
      cancelled = true
      unlisteners.forEach(unlisten => unlisten())
    }
    // navigate is stable (react-router useNavigate identity)
  }, [navigate])

  // Push a fresh snapshot whenever the timer's identity/pause state, the
  // assistant's thinking state, the current route, or the org changes.
  // The bridge subscribes to timer state only; calculate elapsed at emission
  // time and let the companion's display clock advance between snapshots.
  const timerSignature = activeEntry
    ? `${activeEntry.id}:${activeEntry.isPaused ? 1 : 0}:${activeEntry.startTime}:${activeEntry.pausedDuration}:${activeEntry.description ?? ''}`
    : 'none'
  useEffect(() => {
    if (!isTauri()) return
    void emitSyncRef.current().catch(error => {
      logger.error('companion sync failed', error)
    })
  }, [
    timerSignature,
    assistantThinking,
    recordingState,
    orgId,
    orgSlug,
    pageContext,
    companionView,
    userId,
    personality,
    quietUntil,
    favorites,
  ])

  // Route notifications through Benny: forward realtime notifications to the
  // companion as speech bubbles. Native system notifications are suppressed
  // in useBrowserNotifications while this mode is active.
  useEffect(() => {
    if (!isTauri() || !bennyNotificationsEnabled) return undefined

    const handleCreated = (event: Event) => {
      if (!useBennyStore.getState().companionActive) return
      const notification = (event as CustomEvent).detail as Notification
      if (!isOwnedNotificationDeliveryCurrent(notification)) return

      const { orgSlug } = stateRef.current
      const route =
        getNotificationRoute(orgSlug, notification) ??
        (orgSlug ? `/${orgSlug}/notifications` : undefined)
      const payload: CompanionNotificationPayload = {
        id: notification.id,
        title: notification.title || 'New notification',
        message: notification.message ?? undefined,
        route,
        priority: notification.priority,
        createdAt:
          typeof notification.createdAt === 'string'
            ? notification.createdAt
            : undefined,
      }

      void import('@tauri-apps/api/event')
        .then(({ emit }) => {
          if (
            !isOwnedNotificationDeliveryCurrent(notification) ||
            !useBennyStore.getState().companionActive
          )
            return
          return emit(BENNY_COMPANION_EVENTS.notification, payload)
        })
        .catch(() => {
          logger.warn('Could not forward notification to companion')
        })
    }

    window.addEventListener('scrambl:notification-created', handleCreated)
    return () => {
      window.removeEventListener('scrambl:notification-created', handleCreated)
    }
  }, [bennyNotificationsEnabled])

  return null
}
